import { useCallback, useEffect, useMemo, useState } from 'react';
import { carteraApi } from '../api';
import { CATALOGO } from './catalogo';

/**
 * Acceso a los switches de "Ajustes del bot".
 *
 * El catálogo local pone los TEXTOS (título, qué hace, ejemplos de chat); los
 * VALORES vienen del backend:
 *   - GET/POST /bot-ventas/switches → los switches del panel (la base manda
 *     sobre el .env; aplica en ≤ 60 s).
 *   - GET/POST /bot-ventas/config → "Bot encendido" (solo lectura aquí) y
 *     "Recordatorio por silencio" (rescate_activo).
 *
 * 🔴 Sin estado optimista, igual que BotVentasSwitch: mientras se guarda, el
 * switch muestra la posición ANTERIOR con su clave en `guardando`, y solo se
 * repinta con lo que el backend respondió.
 */
export const useBotAjustes = () => {
  const [estado, setEstado] = useState({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(null);

  const porClave = useMemo(
    () => Object.fromEntries(CATALOGO.secciones.flatMap((s) => s.switches).map((s) => [s.clave, s])),
    [],
  );

  useEffect(() => {
    const control = new AbortController();
    Promise.all([
      carteraApi.getBotVentasConfig(control.signal),
      carteraApi.getBotVentasSwitches(control.signal),
    ])
      .then(([config, switches]) => {
        setEstado({ ...deConfig(config.data), ...deSwitches(switches.data) });
      })
      .catch((e) => {
        if (control.signal.aborted) return;
        setError(mensajeDeError(e, 'No se pudieron leer los ajustes del bot.'));
      })
      .finally(() => {
        if (!control.signal.aborted) setCargando(false);
      });
    return () => control.abort();
  }, []);

  const valorDe = useCallback((clave) => Boolean(estado[clave]?.valor), [estado]);
  const servidorDe = useCallback((clave) => Boolean(estado[clave]?.servidor), [estado]);

  const enPanel = useMemo(
    () => Object.fromEntries(Object.entries(estado).filter(([, v]) => v.en_panel).map(([k]) => [k, true])),
    [estado],
  );

  /** Guarda y repinta con la respuesta. Devuelve true/false para el toast. */
  const guardar = useCallback(async (clave, valor) => {
    if (!porClave[clave]?.editable || guardando) return { ok: false };
    setGuardando(clave);
    try {
      if (clave === 'RESCATE') {
        const { data } = await carteraApi.actualizarBotVentasRescate(valor);
        setEstado((prev) => ({ ...prev, ...deConfig(data) }));
      } else {
        const { data } = await carteraApi.guardarBotVentasSwitch(clave, valor);
        setEstado((prev) => ({ ...prev, ...deSwitches(data) }));
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, mensaje: mensajeDeError(e, 'No se pudo guardar. Intenta de nuevo.') };
    } finally {
      setGuardando(null);
    }
  }, [porClave, guardando]);

  const cambiar = useCallback((clave, valor) => guardar(clave, Boolean(valor)), [guardar]);

  // "Regresar al valor del servidor": borra la clave en la base.
  const regresar = useCallback((clave) => guardar(clave, null), [guardar]);

  return {
    cargando,
    error,
    guardando,
    secciones: CATALOGO.secciones,
    porClave,
    cambios: enPanel,
    valorDe,
    servidorDe,
    cambiar,
    regresar,
    cambiados: Object.keys(enPanel).length,
  };
};

/** "Bot encendido" pinta el estado EFECTIVO (env AND panel). */
const deConfig = (data) => {
  const activo = Boolean(data?.entorno?.habilitado_efectivo ?? data?.config?.activo);
  const rescate = Boolean(data?.config?.rescate_activo);
  return {
    ENABLED: { valor: activo, servidor: activo, en_panel: false },
    RESCATE: { valor: rescate, servidor: rescate, en_panel: false },
  };
};

const deSwitches = (data) => Object.fromEntries(
  (Array.isArray(data?.switches) ? data.switches : []).map((s) => [
    s.clave,
    { valor: Boolean(s.valor), servidor: Boolean(s.servidor), en_panel: Boolean(s.en_panel) },
  ]),
);

const mensajeDeError = (error, porDefecto) => {
  const respuesta = error?.response;
  if (respuesta?.data?.message) return respuesta.data.message;
  if (!respuesta) return 'No hay conexión con el servidor. Revisa tu red y vuelve a intentar.';
  return porDefecto;
};

export default useBotAjustes;
